import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Flex, Heading, Text, Badge, Button, Avatar, HStack, VStack,
    Divider, Grid, GridItem, FormControl, FormLabel, Icon, Spinner, useToast,
    IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Select, Tag, Tooltip, Input, useBreakpointValue
} from '@chakra-ui/react';
import {
    FaUserCircle, FaTag, FaFlag, FaRegClock, FaArrowLeft, FaDownload, FaTrash,
    FaComment, FaPlayCircle, FaSyncAlt, FaPauseCircle, FaCheckCircle,
    FaChevronDown, FaEquals, FaChevronUp, FaFire, FaHourglassHalf, FaLink, FaPencilAlt, FaHistory,
    FaPaperclip, FaTimes, FaFile, FaBell
} from 'react-icons/fa';
import { MdOutlineNewReleases, MdBuild, MdBlock, MdCheckCircle } from 'react-icons/md';
import CommentReactions from '../components/CommentReactions';
import TicketNotifyDialog from '../components/TicketNotifyDialog';
import ScrollToTop from '../components/ScrollToTop';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getTicket, addCommentToTicket, downloadTicketAttachment, downloadCommentAttachment, deleteTicketComment, updateTicket, toggleCommentReaction } from '../services/api';
import { WikiEditor, WikiContentViewer, docHasContent } from '../components/WikiEditor';
import { baseUrlApi } from '../config';
import { FaImage } from 'react-icons/fa';

// Resolve storage URL to point to backend host
const storageBaseUrl = baseUrlApi.replace('/api', '');
const resolveStorageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // url comes as "/storage/..." - prepend backend base
    return storageBaseUrl + url;
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const isImageFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.split('.').pop().toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext);
};

const statusConfig = {
    aberto: { color: 'blue', icon: MdOutlineNewReleases, label: 'Aberto', gradient: 'linear(to-r, #3B82F6, #2563EB)' },
    em_andamento: { color: 'orange', icon: MdBuild, label: 'Em Andamento', gradient: 'linear(to-r, #F97316, #EA580C)' },
    bloqueado: { color: 'purple', icon: MdBlock, label: 'Bloqueado', gradient: 'linear(to-r, #8B5CF6, #7C3AED)' },
    fechado: { color: 'green', icon: MdCheckCircle, label: 'Concluido', gradient: 'linear(to-r, #22C55E, #16A34A)' },
};

const priorityConfig = {
    baixa: { color: 'green', icon: FaChevronDown, label: 'Baixa' },
    media: { color: 'yellow', icon: FaEquals, label: 'Media' },
    alta: { color: 'orange', icon: FaChevronUp, label: 'Alta' },
    urgente: { color: 'red', icon: FaFire, label: 'Urgente' },
};

const toggleReactionLocal = (reactions, emoji, userName) => {
    const list = Array.isArray(reactions) ? reactions.map(r => ({ ...r, users: [...(r.users || [])] })) : [];
    const idx = list.findIndex(r => r.emoji === emoji);
    if (idx === -1) {
        return [...list, { emoji, count: 1, reacted: true, users: [userName] }];
    }
    const r = list[idx];
    if (r.reacted) {
        const newCount = r.count - 1;
        if (newCount <= 0) return list.filter((_, i) => i !== idx);
        return list.map((x, i) => i === idx ? { ...x, count: newCount, reacted: false, users: (x.users || []).filter(u => u !== userName) } : x);
    }
    return list.map((x, i) => i === idx ? { ...x, count: x.count + 1, reacted: true, users: [...(x.users || []), userName] } : x);
};

const setCommentReactions = (ticket, commentId, updater) => {
    if (!ticket || !Array.isArray(ticket.updates)) return ticket;
    return {
        ...ticket,
        updates: ticket.updates.map(u =>
            (u.type === 'comment' && u.id === commentId) ? { ...u, reactions: updater(u.reactions || []) } : u
        ),
    };
};

const getImgUser = (usuario_id) => {
    const map = {
        3: "/gabriel_logo.webp", 5: "/tilio_logo.webp", 6: "/riccioppo_logo.webp",
        7: "/user.png", 8: "/gabriel_valiente_logo.webp", 9: "/emerson_vendedor_logo.webp",
        10: "/analuiza_logo.webp"
    };
    return map[usuario_id] || "/user.png";
};

const TicketDetailPage = () => {
    const { ticketId } = useParams();
    const toast = useToast();
    const fileInputRef = useRef(null);
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [commentKey, setCommentKey] = useState(0);
    const [commentFiles, setCommentFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openTime, setOpenTime] = useState('');
    const [downloadingId, setDownloadingId] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [isDeletingComment, setIsDeletingComment] = useState(false);
    const [currentStatus, setCurrentStatus] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isNotifyOpen, setIsNotifyOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    const isMobile = useBreakpointValue({ base: true, lg: false });

    useEffect(() => {
        const userString = window.localStorage.getItem("user");
        if (userString) { try { setCurrentUser(JSON.parse(userString)); } catch {} }
    }, []);

    const fetchTicketDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getTicket(ticketId);
            setTicket(response.data.data);
            setCurrentStatus(response.data.data.status);
        } catch {
            toast({ title: "Erro ao buscar detalhes do ticket", status: "error", isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [ticketId, toast]);

    useEffect(() => { fetchTicketDetails(); }, [fetchTicketDetails]);

    useEffect(() => {
        if (ticket?.created_at) {
            const update = () => setOpenTime(formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true, locale: ptBR }));
            update();
            const id = setInterval(update, 60000);
            return () => clearInterval(id);
        }
    }, [ticket?.created_at]);

    const handleStatusChange = async (newStatus) => {
        if (!ticket || newStatus === ticket.status) return;
        setIsUpdatingStatus(true);
        setCurrentStatus(newStatus);

        const formData = new FormData();
        formData.append('_method', 'PUT');
        formData.append('status', newStatus);

        if (ticket.category) {
            formData.append('categoryIds[]', ticket.category.id);
        } else if (ticket.categories?.length > 0) {
            ticket.categories.forEach(c => formData.append('categoryIds[]', c.id));
        }

        try {
            await updateTicket(ticketId, formData);
            toast({ title: "Status atualizado!", status: "success", isClosable: true });
            await fetchTicketDetails();
        } catch (error) {
            toast({ title: "Erro ao atualizar status.", description: error.response?.data?.message || "", status: "error", isClosable: true });
            setCurrentStatus(ticket.status);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment && commentFiles.length === 0) return;
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('comment', newComment || '.');
            commentFiles.forEach(file => formData.append('files[]', file));
            await addCommentToTicket(ticketId, formData);
            setNewComment('');
            setCommentKey(k => k + 1);
            setCommentFiles([]);
            toast({ title: "Comentario adicionado!", status: "success", isClosable: true });
            await fetchTicketDetails();
        } catch {
            toast({ title: "Erro ao adicionar comentario.", status: "error", isClosable: true });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setCommentFiles(prev => [...prev, ...files]);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setCommentFiles(prev => prev.filter((_, i) => i !== index));
    };

    const openDeleteConfirmation = (commentId) => { setCommentToDelete(commentId); setIsDeleteModalOpen(true); };
    const closeDeleteConfirmation = () => { setCommentToDelete(null); setIsDeleteModalOpen(false); };

    const confirmDeleteComment = async (commentId) => {
        if (!commentId) return;
        setIsDeletingComment(true);
        try {
            await deleteTicketComment(commentId);
            toast({ title: "Comentario removido!", status: "success", isClosable: true });
            await fetchTicketDetails();
        } catch (error) {
            toast({ title: "Erro ao remover comentario.", description: error.response?.data?.message || "", status: "error", isClosable: true });
        } finally {
            setIsDeletingComment(false);
            closeDeleteConfirmation();
        }
    };

    const handleDownload = async (fileId, fileName, isComment = false) => {
        setDownloadingId(fileId);
        try {
            const response = isComment ? await downloadCommentAttachment(fileId) : await downloadTicketAttachment(fileId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch {
            toast({ title: "Erro no Download", status: "error", isClosable: true });
        } finally {
            setDownloadingId(null);
        }
    };

    const formatHistoryValue = (value) => {
        if (!value) return '';
        return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const handleToggleReaction = async (commentId, emoji) => {
        const userName = currentUser?.name || 'Você';
        // Atualizacao otimista: aplica na hora, sem esperar a request
        setTicket(prev => setCommentReactions(prev, commentId, (r) => toggleReactionLocal(r, emoji, userName)));
        try {
            const { data } = await toggleCommentReaction(commentId, emoji);
            // reconcilia com o servidor (fonte da verdade)
            setTicket(prev => setCommentReactions(prev, commentId, () => data.reactions));
        } catch {
            toast({ title: "Erro ao reagir ao comentario", status: "error", isClosable: true });
            fetchTicketDetails();
        }
    };

    const renderUpdate = (update) => {
        const updateTime = format(new Date(update.created_at), "dd/MM/yyyy 'as' HH:mm");

        if (update.type === 'comment') {
            const canDelete = currentUser && update.user && currentUser.id === update.user.id;
            const attachments = update.attachments || [];
            return (
                <Flex key={`comment-${update.id}`} align="start" w="full" gap={3}>
                    <Avatar size="sm" name={update.user?.name} src={getImgUser(update.user?.id)} mt={1} border="2px solid white" boxShadow="0 0 0 1px #E2E8F0" flexShrink={0} />
                    <Box bg="white" p={4} borderRadius="xl" w="full" border="1px solid" borderColor="gray.100" boxShadow="0 1px 3px rgba(0,0,0,0.04)">
                        <Flex justify="space-between" align="center" mb={2}>
                            <HStack spacing={2}>
                                <Text fontWeight="700" fontSize="sm" color="gray.800">{update.user?.name || 'Usuario'}</Text>
                                <Text fontSize="xs" color="gray.400">{updateTime}</Text>
                            </HStack>
                            {canDelete && (
                                <Tooltip label="Remover comentario" hasArrow>
                                    <IconButton icon={<FaTrash />} size="xs" variant="ghost" colorScheme="red" aria-label="Remover" onClick={() => openDeleteConfirmation(update.id)} />
                                </Tooltip>
                            )}
                        </Flex>
                        <WikiContentViewer content={update.comment || ''} />
                        {attachments.length > 0 && (
                            <Flex mt={3} gap={2} flexWrap="wrap">
                                {attachments.map(file => {
                                    if (isImageFile(file.name) && file.url) {
                                        return (
                                            <Tooltip key={file.id} label={file.name} hasArrow>
                                                <Box
                                                    boxSize="64px"
                                                    borderRadius="lg"
                                                    overflow="hidden"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    cursor="pointer"
                                                    transition="all 0.15s"
                                                    _hover={{ borderColor: 'blue.400', boxShadow: '0 0 0 2px rgba(59,130,246,0.3)', transform: 'scale(1.05)' }}
                                                    onClick={(e) => { e.stopPropagation(); setPreviewImage({ url: resolveStorageUrl(file.url), name: file.name }); }}
                                                    flexShrink={0}
                                                    position="relative"
                                                >
                                                    <img src={resolveStorageUrl(file.url)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    <Flex position="absolute" bottom={0} left={0} right={0} bg="blackAlpha.600" justify="center" py={0.5}>
                                                        <Icon as={FaDownload} color="white" boxSize="10px" cursor="pointer" onClick={(e) => { e.stopPropagation(); handleDownload(file.id, file.name, true); }} />
                                                    </Flex>
                                                </Box>
                                            </Tooltip>
                                        );
                                    }
                                    return (
                                        <Button
                                            key={file.id} size="xs" variant="outline" colorScheme="blue"
                                            leftIcon={<FaFile />} borderRadius="full"
                                            onClick={(e) => { e.stopPropagation(); handleDownload(file.id, file.name, true); }}
                                            isLoading={downloadingId === file.id}
                                        >
                                            <Text isTruncated maxW="150px">{file.name}</Text>
                                        </Button>
                                    );
                                })}
                            </Flex>
                        )}
                        <CommentReactions
                            reactions={update.reactions || []}
                            onToggle={(emoji) => handleToggleReaction(update.id, emoji)}
                        />
                    </Box>
                </Flex>
            );
        }

        if (update.type === 'history') {
            return (
                <Flex key={`history-${update.id}`} align="center" w="full" px={2} py={1}>
                    <Icon as={FaHistory} color="gray.300" boxSize="14px" mr={3} flexShrink={0} />
                    <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                        <Text as="span" fontWeight="bold" color="gray.600">{update.user?.name || 'Sistema'}</Text>
                        {update.action ? (
                            <>
                                {' '}{update.action.toLowerCase()} de{' '}
                                <Badge variant="subtle" colorScheme="gray" fontSize="2xs" textTransform="capitalize" borderRadius="full">{formatHistoryValue(update.valor_antigo)}</Badge>
                                {' '}para{' '}
                                <Badge variant="subtle" colorScheme="blue" fontSize="2xs" textTransform="capitalize" borderRadius="full">{formatHistoryValue(update.valor_novo)}</Badge>
                            </>
                        ) : ' realizou uma acao.'}
                        <Text as="span" color="gray.400" ml={2}>{updateTime}</Text>
                    </Text>
                </Flex>
            );
        }
        return null;
    };

    if (loading) return <Flex justify="center" align="center" h="100vh" bg="gray.50"><Spinner size="xl" color="orange.400" thickness="3px" /></Flex>;
    if (!ticket) return <Flex justify="center" align="center" h="100vh" bg="gray.50"><Text color="gray.500">Ticket nao encontrado.</Text></Flex>;

    let updatesArray = Array.isArray(ticket?.updates) ? ticket.updates : (Array.isArray(ticket?.updates?.data) ? ticket.updates.data : []);
    const sortedUpdates = [...updatesArray].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const canEdit = currentUser && ticket?.author && currentUser.id === ticket.author.id;
    const sConfig = statusConfig[ticket.status] || statusConfig.aberto;
    const pConfig = priorityConfig[ticket.priority] || priorityConfig.baixa;

    return (
        <>
            <ScrollToTop />
            <Box bg="gray.50" minH="100vh">
                <Flex justify="center" align="start">
                    <Box w={{ base: 'full', lg: '92%', xl: '88%' }} maxW="1500px" p={{ base: 3, md: 5 }}>

                        {/* Header */}
                        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
                            <Button as={RouterLink} to="/tickets" size="sm" leftIcon={<FaArrowLeft />} variant="ghost" color="gray.600">
                                Voltar
                            </Button>
                            {canEdit && (
                                <Button as={RouterLink} to={`/tickets/${ticket.id}/editar`} size="sm" leftIcon={<FaPencilAlt />} colorScheme="yellow" borderRadius="lg">
                                    Editar
                                </Button>
                            )}
                        </Flex>

                        {/* Ticket Title Card */}
                        <Box bg="white" borderRadius="xl" overflow="hidden" boxShadow="0 1px 3px rgba(0,0,0,0.06)" mb={4}>
                            <Box h="4px" bgGradient={sConfig.gradient} />
                            <Box p={{ base: 4, md: 6 }}>
                                <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                                    <Text fontSize="xs" fontFamily="mono" color="gray.400" fontWeight="bold">#{ticket.id}</Text>
                                    <Badge colorScheme={sConfig.color} borderRadius="full" px={2} display="flex" alignItems="center" gap="4px">
                                        <Icon as={sConfig.icon} boxSize="12px" />{sConfig.label}
                                    </Badge>
                                    <Badge colorScheme={pConfig.color} borderRadius="full" px={2} display="flex" alignItems="center" gap="4px">
                                        <Icon as={pConfig.icon} boxSize="10px" />{pConfig.label}
                                    </Badge>
                                    {ticket.category && (
                                        <Tag size="sm" colorScheme="gray" borderRadius="full">{ticket.category.name}</Tag>
                                    )}
                                </Flex>
                                <Heading as="h1" size={{ base: 'sm', md: 'md' }} color="gray.800" wordBreak="break-word" overflowWrap="break-word" lineHeight="1.4">
                                    {ticket.subject}
                                </Heading>
                                {ticket.description && (
                                    <Box mt={4} p={4} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.100">
                                        <WikiContentViewer content={ticket.description} />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Grid templateColumns={{ base: "1fr", lg: "1fr 300px" }} gap={4}>
                            {/* LEFT: Updates + Comment Form */}
                            <GridItem>
                                <VStack spacing={3} align="stretch">
                                    {sortedUpdates.map(renderUpdate)}
                                </VStack>

                                {/* Comment Form with Drag & Drop */}
                                <Box
                                    bg="white"
                                    p={4}
                                    borderRadius="xl"
                                    boxShadow="0 1px 3px rgba(0,0,0,0.06)"
                                    mt={4}
                                    border="2px solid"
                                    borderColor={isDragging ? 'blue.400' : 'gray.100'}
                                    transition="all 0.2s"
                                    position="relative"
                                    onDragEnter={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        dragCounter.current++;
                                        setIsDragging(true);
                                    }}
                                    onDragLeave={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        dragCounter.current--;
                                        if (dragCounter.current === 0) setIsDragging(false);
                                    }}
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onDrop={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        dragCounter.current = 0;
                                        setIsDragging(false);
                                        const files = Array.from(e.dataTransfer.files);
                                        if (files.length > 0) setCommentFiles(prev => [...prev, ...files]);
                                    }}
                                >
                                    {/* Drag overlay */}
                                    {isDragging && (
                                        <Flex
                                            position="absolute" inset={0} bg="blue.50" borderRadius="xl" zIndex={10}
                                            justify="center" align="center" flexDirection="column" gap={2}
                                            border="2px dashed" borderColor="blue.400"
                                            pointerEvents="none"
                                        >
                                            <Icon as={FaPaperclip} boxSize="24px" color="blue.400" />
                                            <Text color="blue.500" fontWeight="600" fontSize="sm">Solte os arquivos aqui</Text>
                                        </Flex>
                                    )}

                                    <Text fontWeight="700" fontSize="sm" color="gray.700" mb={2}>Adicionar comentario</Text>
                                    <WikiEditor
                                        key={commentKey}
                                        value={newComment}
                                        onChange={({ json }) => setNewComment(docHasContent(json) ? JSON.stringify(json) : '')}
                                        placeholder="Escreva o comentario... use '/' para blocos, cole Markdown ou imagens."
                                        minHeight="120px"
                                    />

                                    {/* File previews */}
                                    {commentFiles.length > 0 && (
                                        <Flex mt={2} gap={2} flexWrap="wrap">
                                            {commentFiles.map((file, i) => {
                                                if (isImageFile(file.name)) {
                                                    return (
                                                        <Box key={i} position="relative" boxSize="52px" borderRadius="lg" overflow="hidden" border="1px solid" borderColor="blue.200">
                                                            <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            <IconButton
                                                                icon={<FaTimes />} size="xs" position="absolute" top={0} right={0}
                                                                bg="blackAlpha.600" color="white" minW="16px" h="16px" borderRadius="full"
                                                                _hover={{ bg: 'red.500' }}
                                                                onClick={() => removeFile(i)} aria-label="Remover"
                                                            />
                                                        </Box>
                                                    );
                                                }
                                                return (
                                                    <Tag key={i} size="sm" colorScheme="blue" borderRadius="full" px={2}>
                                                        <Icon as={FaFile} mr={1} boxSize="10px" />
                                                        <Text isTruncated maxW="120px" fontSize="xs">{file.name}</Text>
                                                        <IconButton icon={<FaTimes />} size="xs" variant="ghost" ml={1} minW="16px" h="16px" onClick={() => removeFile(i)} aria-label="Remover" />
                                                    </Tag>
                                                );
                                            })}
                                        </Flex>
                                    )}

                                    <Flex mt={3} justify="space-between" align="center">
                                        <HStack spacing={1}>
                                            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} multiple />
                                            <Tooltip label="Anexar arquivos" hasArrow>
                                                <IconButton
                                                    icon={<FaPaperclip />}
                                                    size="sm"
                                                    variant="ghost"
                                                    colorScheme="gray"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    aria-label="Anexar"
                                                    borderRadius="full"
                                                />
                                            </Tooltip>
                                            <Text fontSize="2xs" color="gray.400">ou arraste arquivos</Text>
                                        </HStack>
                                        <Button size="sm" colorScheme="blue" leftIcon={<FaComment />} onClick={handleAddComment} isLoading={isSubmitting} loadingText="Enviando..." borderRadius="lg">
                                            Enviar
                                        </Button>
                                    </Flex>
                                </Box>
                            </GridItem>

                            {/* RIGHT: Sidebar */}
                            <GridItem>
                                <Box bg="white" borderRadius="xl" boxShadow="0 1px 3px rgba(0,0,0,0.06)" overflow="hidden" position={{ base: 'static', lg: 'sticky' }} top={4}>
                                    {/* Status header */}
                                    <Box bgGradient={sConfig.gradient} px={4} py={3} color="white">
                                        <HStack spacing={2}>
                                            <Icon as={sConfig.icon} boxSize="18px" />
                                            <Text fontWeight="700" fontSize="sm">{sConfig.label}</Text>
                                        </HStack>
                                    </Box>

                                    <VStack spacing={0} align="stretch" p={4}>
                                        {/* Status change */}
                                        <Box pb={3}>
                                            <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" mb={1}>Alterar status</Text>
                                            <HStack>
                                                <Select size="sm" value={currentStatus} onChange={(e) => handleStatusChange(e.target.value)} isDisabled={isUpdatingStatus} borderRadius="lg" flex={1}>
                                                    <option value="aberto">Aberto</option>
                                                    <option value="em_andamento">Em Andamento</option>
                                                    <option value="bloqueado">Bloqueado</option>
                                                    <option value="fechado">Concluido</option>
                                                </Select>
                                                {isUpdatingStatus && <Spinner size="sm" color="blue.500" />}
                                            </HStack>
                                            <Button
                                                mt={2}
                                                size="sm"
                                                w="100%"
                                                variant="outline"
                                                colorScheme="orange"
                                                leftIcon={<FaBell />}
                                                onClick={() => setIsNotifyOpen(true)}
                                                borderRadius="lg"
                                            >
                                                Notificar Equipe
                                            </Button>
                                        </Box>

                                        <Divider />

                                        {/* Priority */}
                                        <Box py={3}>
                                            <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" mb={1}>Prioridade</Text>
                                            <Badge colorScheme={pConfig.color} borderRadius="full" px={2.5} py={0.5} display="inline-flex" alignItems="center" gap="4px">
                                                <Icon as={pConfig.icon} boxSize="10px" />{pConfig.label}
                                            </Badge>
                                        </Box>

                                        <Divider />

                                        {/* Info */}
                                        <VStack align="start" spacing={2.5} py={3} fontSize="xs">
                                            <HStack spacing={2}>
                                                <Icon as={FaUserCircle} color="gray.400" boxSize="14px" />
                                                <Box>
                                                    <Text color="gray.400" fontWeight="600">Autor</Text>
                                                    <HStack spacing={1.5} mt={0.5}>
                                                        <Avatar size="2xs" name={ticket.author?.name} src={getImgUser(ticket.author?.id)} />
                                                        <Text color="gray.700" fontWeight="medium">{ticket.author?.name}</Text>
                                                    </HStack>
                                                </Box>
                                            </HStack>
                                            <HStack spacing={2}>
                                                <Icon as={FaRegClock} color="gray.400" boxSize="14px" />
                                                <Box>
                                                    <Text color="gray.400" fontWeight="600">Criado em</Text>
                                                    <Text color="gray.700" fontWeight="medium">{format(new Date(ticket.created_at), "dd/MM/yyyy HH:mm")}</Text>
                                                </Box>
                                            </HStack>
                                            {ticket.due_date && (
                                                <HStack spacing={2}>
                                                    <Icon as={FaFlag} color="orange.400" boxSize="14px" />
                                                    <Box>
                                                        <Text color="gray.400" fontWeight="600">Prazo</Text>
                                                        <Text color="orange.600" fontWeight="medium">{format(new Date(ticket.due_date), "dd/MM/yyyy")}</Text>
                                                    </Box>
                                                </HStack>
                                            )}
                                            <HStack spacing={2}>
                                                <Icon as={FaHourglassHalf} color="blue.400" boxSize="14px" />
                                                <Box>
                                                    <Text color="gray.400" fontWeight="600">Tempo aberto</Text>
                                                    <Text color="blue.600" fontWeight="medium">{openTime}</Text>
                                                </Box>
                                            </HStack>
                                        </VStack>

                                        <Divider />

                                        {/* Assignees */}
                                        <Box py={3}>
                                            <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" mb={2}>Atribuido a</Text>
                                            {ticket.assignees?.length > 0 ? (
                                                <VStack align="start" spacing={1.5}>
                                                    {ticket.assignees.map(user => (
                                                        <HStack key={user.id} spacing={2}>
                                                            <Avatar size="xs" name={user.name} src={getImgUser(user.id)} />
                                                            <Text fontSize="xs" fontWeight="medium" color="gray.700">{user.name}</Text>
                                                        </HStack>
                                                    ))}
                                                </VStack>
                                            ) : <Text fontSize="xs" color="gray.400">Ninguem atribuido</Text>}
                                        </Box>

                                        {/* Linked Items */}
                                        {ticket.linkedItems?.length > 0 && (
                                            <>
                                                <Divider />
                                                <Box py={3}>
                                                    <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" mb={2}>Itens Vinculados</Text>
                                                    <VStack align="start" spacing={1}>
                                                        {ticket.linkedItems.map((item, i) => (
                                                            <HStack key={i} spacing={1.5}>
                                                                <Icon as={FaLink} color="gray.400" boxSize="10px" />
                                                                <Text fontSize="xs" color="gray.700">{item}</Text>
                                                            </HStack>
                                                        ))}
                                                    </VStack>
                                                </Box>
                                            </>
                                        )}

                                        {/* Attachments */}
                                        {ticket.attachments?.length > 0 && (
                                            <>
                                                <Divider />
                                                <Box py={3}>
                                                    <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" mb={2}>Anexos do Ticket</Text>
                                                    <Flex gap={2} flexWrap="wrap">
                                                        {ticket.attachments.map(file => {
                                                            if (isImageFile(file.name) && file.url) {
                                                                return (
                                                                    <Tooltip key={file.id} label={`${file.name} (${file.size})`} hasArrow>
                                                                        <Box
                                                                            boxSize="56px"
                                                                            borderRadius="lg"
                                                                            overflow="hidden"
                                                                            border="1px solid"
                                                                            borderColor="gray.200"
                                                                            cursor="pointer"
                                                                            transition="all 0.15s"
                                                                            _hover={{ borderColor: 'blue.400', boxShadow: '0 0 0 2px rgba(59,130,246,0.3)', transform: 'scale(1.05)' }}
                                                                            onClick={() => setPreviewImage({ url: resolveStorageUrl(file.url), name: file.name })}
                                                                            flexShrink={0}
                                                                            position="relative"
                                                                        >
                                                                            <img src={resolveStorageUrl(file.url)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                            <Flex position="absolute" bottom={0} left={0} right={0} bg="blackAlpha.600" justify="center" py={0.5}>
                                                                                <Icon as={FaDownload} color="white" boxSize="9px" cursor="pointer" onClick={(e) => { e.stopPropagation(); handleDownload(file.id, file.name); }} />
                                                                            </Flex>
                                                                        </Box>
                                                                    </Tooltip>
                                                                );
                                                            }
                                                            return (
                                                                <Button
                                                                    key={file.id} size="xs" variant="ghost" colorScheme="blue"
                                                                    leftIcon={<FaDownload />} borderRadius="lg"
                                                                    onClick={() => handleDownload(file.id, file.name)}
                                                                    isLoading={downloadingId === file.id}
                                                                    isDisabled={downloadingId !== null}
                                                                    w="100%" justifyContent="flex-start"
                                                                    style={{ whiteSpace: 'normal', textAlign: 'left' }}
                                                                >
                                                                    <Text fontSize="xs" isTruncated>{file.name} <Text as="span" color="gray.400">({file.size})</Text></Text>
                                                                </Button>
                                                            );
                                                        })}
                                                    </Flex>
                                                </Box>
                                            </>
                                        )}
                                    </VStack>
                                </Box>
                            </GridItem>
                        </Grid>
                    </Box>
                </Flex>
            </Box>

            {/* Notify Dialog */}
            {ticket && (
                <TicketNotifyDialog
                    isOpen={isNotifyOpen}
                    onClose={() => setIsNotifyOpen(false)}
                    ticket={ticket}
                />
            )}

            {/* Image Preview Modal */}
            <Modal isOpen={!!previewImage} onClose={() => setPreviewImage(null)} size="4xl" isCentered>
                <ModalOverlay bg="blackAlpha.700" />
                <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
                    <ModalCloseButton color="white" bg="blackAlpha.500" borderRadius="full" top={2} right={2} zIndex={10} _hover={{ bg: 'blackAlpha.700' }} />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {previewImage && (
                            <Box borderRadius="xl" overflow="hidden" maxH="85vh">
                                <img src={previewImage.url} alt={previewImage.name} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }} />
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteModalOpen} onClose={isDeletingComment ? () => {} : closeDeleteConfirmation} isCentered>
                <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
                <ModalContent mx={4} borderRadius="xl">
                    <ModalHeader fontSize="md">Confirmar Exclusao</ModalHeader>
                    <ModalCloseButton isDisabled={isDeletingComment} />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">Tem certeza que deseja remover este comentario? Esta acao nao pode ser desfeita.</Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" size="sm" mr={2} onClick={closeDeleteConfirmation} isDisabled={isDeletingComment} borderRadius="lg">Cancelar</Button>
                        <Button colorScheme="red" size="sm" onClick={() => confirmDeleteComment(commentToDelete)} isLoading={isDeletingComment} loadingText="Removendo..." borderRadius="lg">Remover</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default TicketDetailPage;
